import React from 'react';
import {
  Tooltip,
  OverlayTrigger,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useHistory } from 'react-router-dom';

import notFound from '../../assets/notFound.png';
import {
  addToCompare,
  removeFromCompare,
  selectCompareIds,
  MAX_COMPARE_PLAYERS,
} from '../../shared/store/compareSlice';
import trackEvent from '../../shared/utils/eventTracking';
import { toasterOn } from '../../shared/store/actions';

import './Player.scss';

const renderSportIcon = sport => {
  switch (sport) {
    case 'Volleyball':
      return 'fa-volleyball-ball';
    case 'Soccer':
      return 'fa-futbol-o';
    case 'Beach Volleyball':
      return 'fa-volleyball-ball';
    case 'Tennis':
      return 'fa-tennis-o';
    case 'Basketball':
      return 'fa-basketball-ball';
    case 'Water Polo':
    case 'Swimming':
      return 'fa-swimmer';
    case 'Track & Field':
      return 'fa-running';
    case 'Ice Hockey':
      return 'fa-hockey-puck';

    default:
      return '';
  }
};

const Player = ({ player, changeFavouriteStatus, onDeletePlayer }) => {
  const { userRole, favouritesPlayers } = useSelector(
    ({ authorization }) => authorization,
  );
  const comparePlayerIds = useSelector(selectCompareIds);
  const pathname = window.location.pathname;
  const viewSource = pathname === '/favorite-players' ? 'favorites' : 'list';

  const {
    _id,
    firstName,
    lastName,
    transfer,
    levelOfPlay,
    position,
    graduationYear,
    height,
    image,
    sport,
  } = player;

  const dispatch = useDispatch();
  const history = useHistory();

  const checkIfPhotoExists = () => {
    if (image) {
      const buffer = image.data;
      const b64 = Buffer.from(buffer).toString('base64');
      return `data:image/jpeg;base64,${b64}`;
    }
    return notFound;
  };

  const isFavourite = (favouritesPlayers || []).includes(_id);
  const isInCompare = comparePlayerIds.includes(_id);
  const context = pathname === '/favorite-players' ? 'favorites' : 'available_players';

  const handleCompareToggle = e => {
    e.stopPropagation();
    if (isInCompare) {
      dispatch(removeFromCompare(_id));
      trackEvent('compare_remove_player', { playerId: _id, metadata: { context } });
    } else {
      if (comparePlayerIds.length >= MAX_COMPARE_PLAYERS) {
        dispatch(toasterOn({ message: `You can compare up to ${MAX_COMPARE_PLAYERS} players`, type: 'failed' }));
        return;
      }
      dispatch(addToCompare({ _id, firstName, lastName }));
      trackEvent('compare_add_player', { playerId: _id, metadata: { context } });
    }
  };

  return (
    <div className="player-card">
      {/* Image with overlay actions */}
      <div className="player-card__image-container">
        <img 
          src={checkIfPhotoExists()} 
          alt={`${firstName} ${lastName}`}
          className="player-card__image"
          onClick={() => history.push({ pathname: `available-players/${_id}`, state: { source: viewSource } })}
        />
        
        {/* Favorite button — hidden for assistant */}
        {userRole !== 'assistant' && (
          <OverlayTrigger
            overlay={
              <Tooltip>
                {!isFavourite ? 'Add to Favorites' : 'Remove from Favorites'}
              </Tooltip>
            }
          >
            <button
              type="button"
              className={`player-card__fav ${isFavourite ? 'player-card__fav--active' : ''}`}
              onClick={() => changeFavouriteStatus(_id)}
            >
              <i className={isFavourite ? 'fas fa-heart' : 'far fa-heart'} />
            </button>
          </OverlayTrigger>
        )}

        {/* Admin actions */}
        {userRole === 'admin' && (
          <div className="player-card__admin">
            <NavLink to={`/edit/${_id}`} className="player-card__admin-btn">
              <i className="fas fa-edit" />
            </NavLink>
            <button
              type="button"
              className="player-card__admin-btn"
              onClick={() => onDeletePlayer && onDeletePlayer(_id)}
            >
              <i className="fas fa-trash-alt" />
            </button>
          </div>
        )}

        {/* Transfer badge */}
        {transfer && (
          <span className="player-card__transfer">Transfer</span>
        )}
      </div>

      {/* Content */}
      <div className="player-card__body">
        <h3 
          className="player-card__name"
          onClick={() => history.push({ pathname: `available-players/${_id}`, state: { source: viewSource } })}
        >
          {firstName} {lastName}
        </h3>

        <div className="player-card__stats">
          <div className="player-card__stat">
            <span className="player-card__stat-label">Height</span>
            <span className="player-card__stat-value">{height}</span>
          </div>
          <div className="player-card__stat">
            <span className="player-card__stat-label">Available</span>
            <span className="player-card__stat-value">{graduationYear}</span>
          </div>
        </div>

        {!!position?.length && (
          <div className="player-card__info">
            <i className={`fas ${renderSportIcon(sport)}`} />
            <span>{position.join(', ')}</span>
          </div>
        )}

        <div className="player-card__info">
          <i className="fas fa-trophy" />
          <span>{levelOfPlay?.length ? levelOfPlay.join(', ') : '-'}</span>
        </div>

        <div className="player-card__actions-row">
          {userRole !== 'assistant' && (
            <button
              type="button"
              className={`player-card__compare ${isInCompare ? 'player-card__compare--active' : ''}`}
              onClick={handleCompareToggle}
              title={isInCompare ? 'Remove from compare' : 'Add to compare'}
            >
              <i className={isInCompare ? 'fas fa-check-square' : 'far fa-clone'} />
              {isInCompare ? 'Added' : 'Compare'}
            </button>
          )}
          <button
            type="button"
            className="player-card__btn"
            onClick={() => history.push({ pathname: `available-players/${_id}`, state: { source: viewSource } })}
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default Player;
